import React, { useEffect, useRef, useState } from "react";
import LiveReactionLayer from "./LiveReactionLayer";
import {
  getDonationEffectSpec,
  normalizeDonationLeadingEmojis,
} from "../config/donationEffects";

export const MAX_SHOWME_DONATION_DOLLARS = 2_000;
const SHOWME_DOLLAR_SOURCE_HISTORY = 2_500;

export const getShowMeDonationDollarCount = (amount, paymentEmoji) => (
  getDonationEffectSpec(amount, paymentEmoji).dollarCount
);

export const normalizeShowMeDonationDollarCount = (count) => {
  const numericCount = Math.round(Number(count) || 0);
  return Math.min(MAX_SHOWME_DONATION_DOLLARS, Math.max(0, numericCount));
};

const stableDollarSize = (effectId, index) => {
  const seed = `${effectId}:${index}`;
  let hash = 2166136261;
  for (let offset = 0; offset < seed.length; offset += 1) {
    hash ^= seed.charCodeAt(offset);
    hash = Math.imul(hash, 16777619);
  }
  return 28 + (Math.abs(hash) % 39);
};

export const buildShowMeDonationDollarReactions = (effect) => {
  const effectId = String(effect?.id || "donation");
  const leadingEmojis = normalizeDonationLeadingEmojis(effect?.leadingEmojis);
  const count = normalizeShowMeDonationDollarCount(effect?.dollarCount);
  const leadingReactions = leadingEmojis.map((emoji, index) => ({
    id: `showme-lead:${effectId}:${index}`,
    emoji,
  }));
  const dollarReactions = Array.from({ length: count }, (_, index) => ({
    id: `showme-dollar:${effectId}:${index}`,
    emoji: "$",
    glyphColor: "#22c55e",
    glyphSize: stableDollarSize(effectId, index),
    glyphWeight: 900,
    glyphShadow: "0 4px 16px rgba(0,0,0,.58), 0 0 14px rgba(34,197,94,.9)",
  }));
  return [...leadingReactions, ...dollarReactions];
};

const ShowMeDonationDollarLayer = ({
  events = [],
  className = "z-40",
}) => {
  const [reactions, setReactions] = useState([]);
  const seenEffectIdsRef = useRef(new Set());

  useEffect(() => {
    const additions = [];
    events.forEach((effect) => {
      const effectId = String(effect?.id || "");
      if (!effectId || seenEffectIdsRef.current.has(effectId)) return;
      seenEffectIdsRef.current.add(effectId);
      additions.push(...buildShowMeDonationDollarReactions(effect));
    });
    if (!additions.length) return;

    // LiveReactionLayer owns the paced launch queue and keeps at most 70
    // particles mounted. Preserve one complete maximum-size burst plus enough
    // prior IDs for deduplication without rendering every queued object.
    setReactions((current) => (
      [...current.slice(-600), ...additions].slice(-SHOWME_DOLLAR_SOURCE_HISTORY)
    ));
    if (seenEffectIdsRef.current.size > 500) {
      seenEffectIdsRef.current = new Set([...seenEffectIdsRef.current].slice(-250));
    }
  }, [events]);

  return (
    <LiveReactionLayer
      reactions={reactions}
      className={className}
      side="right"
      launchGapMs={70}
      launchJitterMs={35}
      maxActive={70}
      dataVariant="showme-donation-dollars"
    />
  );
};

export default ShowMeDonationDollarLayer;
